import { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Input,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
  type TableProps,
} from 'antd';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus, RefreshCw, Search, ShieldOff } from 'lucide-react';
import {
  iamApi as iamHttpApi,
  type IamPolicyGrantIdentityType,
  type IamPolicyGrantRecord,
  type SearchPolicyGrantsParams,
} from '@/api/iam.api';
import ProTable from '@/components/pro/ProTable';
import { GrantPermissionDrawer, type GrantPermissionPreset } from '@/pages/iam/components';
import { debounce } from '@/utils/utils';
import GrantDetail from './GrantDetail';
import './grants.css';

const PAGE_SIZE = 20;
type GrantSortField = 'authorization_at';

const IDENTITY_TYPE_LABELS: Record<IamPolicyGrantIdentityType, string> = {
  user_group: '用户组',
  user: '用户',
};

const RANGE_LABELS: Record<string, string> = {
  self: '我创建的资源',
  all: '租户全部资源',
};

type PolicyTypeFilter = 'System' | 'Custom';

function getGrantKey(grant: IamPolicyGrantRecord): string {
  return JSON.stringify([grant.identity_type, grant.identity, grant.policy_id]);
}

function getRangeLabel(range: string): string {
  return RANGE_LABELS[range.toLowerCase()] || range;
}

export default function GrantList() {
  const { message } = App.useApp();
  const [keyword, setKeyword] = useState('');
  const [queryKeyword, setQueryKeyword] = useState('');
  const [identityTypeFilter, setIdentityTypeFilter] = useState<
    IamPolicyGrantIdentityType | undefined
  >();
  const [policyTypeFilter, setPolicyTypeFilter] = useState<PolicyTypeFilter | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [orderBy, setOrderBy] = useState<GrantSortField | `-${GrantSortField}`>(
    '-authorization_at'
  );
  const [grantDrawer, setGrantDrawer] = useState<{ preset?: GrantPermissionPreset } | null>(null);
  const [detailGrant, setDetailGrant] = useState<IamPolicyGrantRecord>();
  const [expandedGrantRuleIds, setExpandedGrantRuleIds] = useState<Set<string>>(new Set());
  const [preparingRevokeGrantKey, setPreparingRevokeGrantKey] = useState<string>();
  const [revokeConfirmation, setRevokeConfirmation] = useState<{
    grantKey: string;
    affectedUserCount: number;
  }>();
  const [revokingGrantKey, setRevokingGrantKey] = useState<string>();

  const updateQueryKeyword = useMemo(
    () =>
      debounce((value: string) => {
        setQueryKeyword(value);
        setPage(1);
      }, 300),
    []
  );

  useEffect(() => () => updateQueryKeyword.cancel(), [updateQueryKeyword]);

  const query = useMemo<SearchPolicyGrantsParams>(
    () => ({
      limit: pageSize,
      offset: String((page - 1) * pageSize),
      order_by: orderBy,
      ...(queryKeyword.trim() ? { search: queryKeyword.trim() } : {}),
      ...(identityTypeFilter ? { identity_type: identityTypeFilter } : {}),
      ...(policyTypeFilter ? { policy_type: policyTypeFilter } : {}),
    }),
    [page, pageSize, queryKeyword, identityTypeFilter, policyTypeFilter, orderBy]
  );

  const {
    data: grantsResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['iam', 'grants', 'list', query] as const,
    queryFn: async () => {
      const response = await iamHttpApi.searchPolicyGrants(query);
      if (response.code !== 0) {
        throw new Error(response.message || '查询策略授权记录失败');
      }
      return response;
    },
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const grants = useMemo(() => grantsResponse?.data.records ?? [], [grantsResponse]);

  const prepareRevoke = async (row: IamPolicyGrantRecord) => {
    const rowKey = getGrantKey(row);
    if (row.identity_type !== 'user_group') {
      setRevokeConfirmation({ grantKey: rowKey, affectedUserCount: 1 });
      return;
    }

    setPreparingRevokeGrantKey(rowKey);
    try {
      const group = await iamHttpApi.getIAMUserGroup({ id: row.identity });
      setRevokeConfirmation({
        grantKey: rowKey,
        affectedUserCount: group.edges.users?.length ?? 0,
      });
    } catch {
      // onOpenChange 以 fire-and-forget 方式调用，需消费 rejection，错误提示由请求层处理。
    } finally {
      setPreparingRevokeGrantKey(undefined);
    }
  };

  const doRevoke = async (row: IamPolicyGrantRecord) => {
    const rowKey = getGrantKey(row);
    setRevokingGrantKey(rowKey);
    try {
      const response = await iamHttpApi.revokePolicyGrant({
        policy_ids: [row.policy_id],
        identities: [{ identity_type: row.identity_type, identity: row.identity }],
      });
      if (response.code !== 0) {
        throw new Error(response.message || '撤销策略授权失败');
      }
      if (detailGrant && getGrantKey(detailGrant) === rowKey) setDetailGrant(undefined);
      message.success('授权已撤销');
      await refetch();
    } finally {
      setRevokingGrantKey(undefined);
    }
  };

  const columns: TableColumnsType<IamPolicyGrantRecord> = [
    {
      title: '授权主体',
      width: 210,
      render: (_, row) => (
        <div className="iam-grant-name-cell">
          <Typography.Text strong>{row.identity_name || row.identity}</Typography.Text>
          <Typography.Text type="secondary">{row.identity}</Typography.Text>
        </div>
      ),
    },
    {
      title: '主体类别',
      width: 110,
      render: (_, row) => IDENTITY_TYPE_LABELS[row.identity_type] || row.identity_type,
    },
    {
      title: '权限策略',
      width: 240,
      render: (_, row) => (
        <div className="iam-grant-policy-cell">
          <Typography.Text>{row.policy_name}</Typography.Text>
          <span>
            <Tag color={row.type === 'system' ? 'blue' : 'purple'} bordered={false}>
              {row.type === 'system' ? '系统策略' : '自定义'}
            </Tag>
          </span>
        </div>
      ),
    },
    {
      title: '策略描述',
      width: 360,
      render: (_, row) =>
        row.policy_description ? (
          <Tooltip title={row.policy_description}>
            <Typography.Text ellipsis={{ tooltip: row.policy_description }}>
              {row.policy_description}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '策略描述',
      width: 360,
      render: (_, row) =>
        row.policy_description ? (
          <Tooltip title={row.policy_description}>
            <Typography.Text ellipsis={{ tooltip: row.policy_description }}>
              {row.policy_description}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '策略描述',
      width: 360,
      render: (_, row) =>
        row.policy_description ? (
          <Tooltip title={row.policy_description}>
            <Typography.Text ellipsis={{ tooltip: row.policy_description }}>
              {row.policy_description}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '策略描述',
      width: 360,
      render: (_, row) =>
        row.policy_description ? (
          <Tooltip title={row.policy_description}>
            <Typography.Text ellipsis={{ tooltip: row.policy_description }}>
              {row.policy_description}
            </Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '授权范围',
      width: 340,
      render: (_, row) => {
        const rowKey = getGrantKey(row);
        const expanded = expandedGrantRuleIds.has(rowKey);
        const visibleRanges = expanded
          ? row.authorization_range
          : row.authorization_range.slice(0, 2);
        return row.authorization_range.length ? (
          <div className="iam-grant-rule-list">
            {visibleRanges.map((range, index) => {
              const resourceName = range.display_name || range.resource;
              const label = `${resourceName} · ${getRangeLabel(range.range)}`;
              return (
                <div
                  className="iam-grant-rule"
                  key={`${range.service}-${range.resource}-${range.range}-${index}`}
                >
                  <Tag bordered={false}>{range.service}</Tag>
                  <Typography.Text ellipsis={{ tooltip: label }}>{label}</Typography.Text>
                </div>
              );
            })}
            {row.authorization_range.length > 2 && (
              <Button
                className="iam-grant-rule-more"
                type="link"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setExpandedGrantRuleIds((current) => {
                    const next = new Set(current);
                    if (next.has(rowKey)) next.delete(rowKey);
                    else next.add(rowKey);
                    return next;
                  });
                }}
              >
                {expanded ? '收起范围' : `+${row.authorization_range.length - 2} 个资源`}
              </Button>
            )}
          </div>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        );
      },
    },
    {
      title: '授权时间',
      key: 'authorization_at',
      width: 170,
      sorter: true,
      sortOrder:
        orderBy === 'authorization_at'
          ? 'ascend'
          : orderBy === '-authorization_at'
            ? 'descend'
            : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, row) => new Date(row.authorization_at * 1000).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right',
      render: (_, row) => {
        const rowKey = getGrantKey(row);
        const affectedUserCount =
          revokeConfirmation?.grantKey === rowKey ? revokeConfirmation.affectedUserCount : 0;
        return (
          <span onClick={(event) => event.stopPropagation()}>
            <Popconfirm
              open={revokeConfirmation?.grantKey === rowKey}
              title="撤销授权"
              description={`只撤销这条授权记录，将影响 ${affectedUserCount} 名用户；其他直接或用户组授权不受影响。`}
              okText="撤销"
              okButtonProps={{ danger: true, loading: revokingGrantKey === rowKey }}
              onConfirm={() => doRevoke(row)}
              onCancel={() => setRevokeConfirmation(undefined)}
              onOpenChange={(open) => {
                if (open) void prepareRevoke(row);
                else setRevokeConfirmation(undefined);
              }}
            >
              <Tooltip title="撤销授权">
                <Button
                  type="text"
                  danger
                  loading={preparingRevokeGrantKey === rowKey}
                  aria-label={`撤销 ${row.identity} 的授权`}
                  icon={<ShieldOff size={15} />}
                />
              </Tooltip>
            </Popconfirm>
          </span>
        );
      },
    },
  ];

  const handleTableChange: TableProps<IamPolicyGrantRecord>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action !== 'sort') return;
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = activeSorter?.columnKey;
    if (!activeSorter?.order || field !== 'authorization_at') return;
    setOrderBy(activeSorter.order === 'ascend' ? field : `-${field}`);
    setPage(1);
  };

  return (
    <div className="pp-page iam-page">
      <header className="pp-head">
        <h2>授权</h2>
        <Space>
          <Button icon={<RefreshCw size={14} />} loading={isFetching} onClick={() => refetch()}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => setGrantDrawer({ preset: undefined })}
          >
            新建授权
          </Button>
        </Space>
      </header>

      <div className="iam-grant-toolbar">
        <Input
          aria-label="搜索关键词"
          className="iam-grant-search"
          allowClear
          prefix={<Search size={14} />}
          placeholder="搜索主体名称 / 主体 ID / 策略名称"
          value={keyword}
          onChange={(event) => {
            const value = event.target.value;
            setKeyword(value);
            updateQueryKeyword(value);
          }}
        />
        <Select
          className="iam-grant-filter"
          allowClear
          placeholder="主体类型"
          value={identityTypeFilter}
          onChange={(next) => {
            setIdentityTypeFilter(next);
            setPage(1);
          }}
          options={[
            { value: 'user', label: '用户' },
            { value: 'user_group', label: '用户组' },
          ]}
        />
        <Select
          className="iam-grant-filter"
          allowClear
          placeholder="策略类型"
          value={policyTypeFilter}
          onChange={(next) => {
            setPolicyTypeFilter(next);
            setPage(1);
          }}
          options={[
            { value: 'System', label: '系统策略' },
            { value: 'Custom', label: '自定义策略' },
          ]}
        />
      </div>

      <ProTable<IamPolicyGrantRecord>
        data={grants}
        loading={isLoading}
        tableLoading={isFetching && !isLoading}
        error={error ? (error instanceof Error ? error.message : String(error)) : null}
        columns={columns}
        onChange={handleTableChange}
        rowKey={getGrantKey}
        tableLayout="fixed"
        scroll={{ x: 1150 }}
        onRetry={refetch}
        onRow={(row) => ({
          onClick: () => setDetailGrant(row),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize,
          total: grantsResponse?.data.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (count) => `共 ${count} 条`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
      />

      <GrantPermissionDrawer
        open={!!grantDrawer}
        preset={grantDrawer?.preset}
        onClose={() => setGrantDrawer(null)}
        onSuccess={async () => {
          await refetch();
        }}
      />

      <GrantDetail
        open={!!detailGrant}
        grant={detailGrant}
        onClose={() => setDetailGrant(undefined)}
      />
    </div>
  );
}
